import { MockDatabase, MockRecord, MockTable, TableQuery } from "../MockDatabase";

export type BundleListAPI = {
    bundle_guid: string;
    display_name: string;
    type: string;
    created_date: string;
    updated_date: string;
}

export type BundleAPI = {
    bundle_guid: string;
    display_name: string;
    type: string;
    created_date: string;
    updated_date: string;
    organization_guid: string;
    parent_guid: string | null;
    children: BundleListAPI[];
}

export class MockBundles extends MockTable {
    constructor(db: MockDatabase) {
        super(db);
        this.name = 'bundles';
        this.fields = {
            id: this.PrimaryKey({
                fieldName: 'id'
            }),
            bundle_guid: this.PublicKey({
                fieldName: 'bundle_guid'
            }),
            organization_id: this.ForeignKey({
                fieldName: 'organization_id',
                nullable: false,
                index: true,
                foreignTableName: 'organizations',
                foreignTableFieldName: 'id',
            }),
            display_name: this.Field({
                type: 'string',
                fieldName: 'display_name',
                nullable: false
            }),
            created_date: this.Field({
                type: 'datetime',
                fieldName: 'created_date',
                nullable: false,
                default: {
                    method: 'NOW'
                }
            }),
            parent_id: this.ForeignKey({
                fieldName: 'parent_id',
                nullable: true,
                index: true,
                foreignTableName: 'bundles',
                foreignTableFieldName: 'id',
            }),
            type: this.Field({
                type: 'string',
                fieldName: 'type',
                nullable: false
            }),
            updated_date: this.Field({
                type: 'datetime',
                fieldName: 'updated_date',
                nullable: false,
                default: {
                    method: 'NOW',
                    onUpdate: true
                }
            }),
        }
    }
    
    private _transformPayload(db: MockDatabase, values: Record<string, any>): Record<string, any> {
        let {
            organization_guid, organizationGuid, organization_id, 
            parent_guid, parentGuid, parent_id, 
            ...data
        } = values;
        if ((organization_guid || organizationGuid) && !organization_id) {
            organization_id = db.resolveGuidToId('organizations', (organization_guid || organizationGuid));
        }
        if ((parent_guid || parentGuid) && !parent_id) {
            parent_id = db.resolveGuidToId('bundles', (parent_guid || parentGuid));
        }
        return {
            ...data,
            ...(parent_id ? {parent_id} : {}),
            ...(organization_id ? {organization_id} : {}),
        }
    }
    
    protected beforeQuery: (db: MockDatabase, table: MockTable, query: TableQuery) => TableQuery = (db, table, query) => {
        const transFormFields: Record<string, any> = {};
        const remQuery: TableQuery = [];
        query.forEach(entry => {
            if (entry.fieldName === 'organization_guid' || entry.fieldName === 'organizationGuid') {
                transFormFields['organization_guid'] = entry.value;
            } else if (entry.fieldName === 'parent_guid' || entry.fieldName === 'parentGuid') {
                transFormFields['parent_guid'] = entry.value;
            } else {
                remQuery.push(entry);
            }
        });
        const fixed = this._transformPayload(db, transFormFields);
        Object.entries(fixed).forEach(([fieldName, value]) => remQuery.unshift({
            fieldName,
            value
        }));
        return remQuery;
    }

    protected beforeUpdate: (db: MockDatabase, table: MockTable, records: MockRecord[], data: Record<string, any>) => Record<string, any> = (db, table, records, data) => {
        return this._transformPayload(db, data);
    }

    protected beforeCreate: (db: MockDatabase, table: MockTable, values: Record<string, any>) => Record<string, any> = (db, table, values) => {
        const createData = this._transformPayload(db, values);
        if (!createData['type']) {
            createData['type'] = 'STANDARD';
        }
        return createData;
    }

    private _baseSerialize = (record: MockRecord) => {
        return {
            bundle_guid: record.values['bundle_guid'],
            display_name: record.values['display_name'],
            type: record.values['type'],
            created_date: record.values['created_date'],
            updated_date: record.values['updated_date'],
        }
    }
    
    serialize: (record: MockRecord) => BundleAPI = (record) => {
        const parentRec = record.getRelated('bundles');
        return {
            ...this._baseSerialize(record),
            organization_guid: record.values['organization_guid'],
            parent_guid: parentRec.length ? parentRec[0].values['bundle_guid'] : undefined,
            children: record.getDependents('bundles').map(rec => this._baseSerialize(rec))
        } as BundleAPI
    }
    
    serializeList = (records: MockRecord[], db: MockDatabase) => records.map(record => {
        return this._baseSerialize(record);
    }) as BundleListAPI[];
}